import React from "react";
import { Link } from "react-router-dom";
import { PilotButton } from "../components/PilotIntake";

/* ------------------------------------------------------------------ */
/* Reusable bits                                                       */
/* ------------------------------------------------------------------ */

function Eyebrow({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] px-3 py-1.5 rounded-full"
      style={{
        color: "var(--primary-color)",
        backgroundColor: "var(--primary-50)",
        border: "1px solid color-mix(in srgb, var(--primary-color) 18%, transparent)",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--primary-color)" }} />
      {children}
    </span>
  );
}

function SectionHeader({ eyebrow, title, sub, center = true }) {
  return (
    <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {eyebrow && (
        <div className={center ? "flex justify-center mb-4" : "mb-4"}>
          <Eyebrow>{eyebrow}</Eyebrow>
        </div>
      )}
      <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
        {title}
      </h2>
      {sub && (
        <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--muted-color)" }}>
          {sub}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

function Hero() {
  return (
    <section
      className="relative pt-16 pb-20 sm:pt-24 sm:pb-28 overflow-hidden"
      style={{ background: "var(--gradient-soft)" }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          <div className="flex justify-center mb-6">
            <Eyebrow>Pilot open · Fall 2026 · Limited campuses</Eyebrow>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight">
            <span style={{ color: "var(--text-color)" }}>Engagement that</span>{" "}
            <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>
              teaches itself.
            </span>
          </h1>

          <p
            className="mt-6 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed"
            style={{ color: "var(--muted-color)" }}
          >
            The modern engagement platform for university classrooms. Live polls, Q&amp;A, participation, and
            AI-generated session summaries, in one quiet, elegant tool.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <PilotButton className="ce-btn ce-btn-primary text-base px-6 py-3.5">
              Join the pilot
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </PilotButton>
            <Link to="/product-detail" className="ce-btn ce-btn-secondary text-base px-6 py-3.5">
              See the product
            </Link>
          </div>

          <div
            className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm"
            style={{ color: "var(--subtle-color)" }}
          >
            <span className="inline-flex items-center gap-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Free during pilot · limited spots
            </span>
            <span className="inline-flex items-center gap-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              LMS grade sync
            </span>
            <span className="inline-flex items-center gap-2">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Privacy-first
            </span>
          </div>
        </div>

        {/* Product mockup */}
        <div className="mt-16 sm:mt-20">
          <ProductMockup />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Product mockup                                                      */
/* ------------------------------------------------------------------ */

function ProductMockup() {
  const poll = [
    { label: "A. Convex up", value: 14, pct: 18 },
    { label: "B. Convex down", value: 51, pct: 65, correct: true },
    { label: "C. Linear", value: 9, pct: 12 },
    { label: "D. None of the above", value: 4, pct: 5 },
  ];

  return (
    <div
      className="relative mx-auto max-w-6xl rounded-2xl overflow-hidden"
      style={{
        backgroundColor: "var(--card-bg)",
        border: "1px solid var(--border-color)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      {/* Window chrome */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--border-color)", backgroundColor: "var(--alt-bg)" }}
      >
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#ff5f57" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#febc2e" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#28c840" }} />
        </div>
        <div className="flex-1 flex justify-center">
          <span
            className="text-xs px-3 py-1 rounded-full"
            style={{
              backgroundColor: "var(--card-bg)",
              color: "var(--muted-color)",
              border: "1px solid var(--border-color)",
            }}
          >
            coursengage.app / professor / live
          </span>
        </div>
        <span className="text-xs" style={{ color: "var(--subtle-color)" }}>
          LIVE
        </span>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-[420px]">
        {/* Sidebar */}
        <aside
          className="hidden lg:flex flex-col p-4 gap-1"
          style={{ borderRight: "1px solid var(--border-color)", backgroundColor: "var(--alt-bg)" }}
        >
          {[
            { label: "Dashboard", icon: "▦" },
            { label: "Live session", icon: "●", active: true },
            { label: "Q&A", icon: "✦" },
            { label: "Participation", icon: "◐" },
            { label: "Grades", icon: "✓" },
            { label: "AI summaries", icon: "✨" },
            { label: "LMS sync", icon: "⇄" },
          ].map((i) => (
            <div
              key={i.label}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm"
              style={{
                color: i.active ? "var(--primary-color)" : "var(--text-soft)",
                backgroundColor: i.active ? "var(--primary-50)" : "transparent",
                fontWeight: i.active ? 600 : 500,
              }}
            >
              <span className="w-4 text-center">{i.icon}</span>
              {i.label}
            </div>
          ))}
          <div
            className="mt-auto p-3 rounded-xl text-xs"
            style={{
              backgroundColor: "var(--card-bg)",
              border: "1px solid var(--border-color)",
              color: "var(--muted-color)",
            }}
          >
            <div className="font-semibold mb-1" style={{ color: "var(--text-color)" }}>
              MATH 221 · Sec 3
            </div>
            134 students · Fall 2026
          </div>
        </aside>

        {/* Main */}
        <div className="p-5 sm:p-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--muted-color)" }}>
                Live poll · Question 4 of 7
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold" style={{ color: "var(--text-color)" }}>
                Which curve describes the function f(x) = x²?
              </h3>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{
                  color: "var(--primary-color)",
                  backgroundColor: "var(--primary-50)",
                  border: "1px solid color-mix(in srgb, var(--primary-color) 25%, transparent)",
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ backgroundColor: "var(--primary-color)" }}
                />
                Live · 78 / 134
              </span>
              <span
                className="text-xs font-mono px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: "var(--alt-bg)",
                  color: "var(--text-soft)",
                  border: "1px solid var(--border-color)",
                }}
              >
                00:42
              </span>
            </div>
          </div>

          {/* Poll bars */}
          <div className="mt-6 space-y-3">
            {poll.map((p) => (
              <div key={p.label}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span style={{ color: "var(--text-color)", fontWeight: 500 }}>
                    {p.label}{" "}
                    {p.correct && (
                      <span
                        className="ml-1.5 text-xs px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: "color-mix(in srgb, var(--success-color) 14%, transparent)",
                          color: "var(--success-color)",
                        }}
                      >
                        correct
                      </span>
                    )}
                  </span>
                  <span style={{ color: "var(--muted-color)" }}>
                    {p.value} · {p.pct}%
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--alt-bg)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${p.pct}%`,
                      background: p.correct ? "var(--success-color)" : "var(--gradient-primary)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Stat strip */}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: "Participation", v: "94%" },
              { k: "Avg. response", v: "11s" },
              { k: "Top Q&A votes", v: "27" },
              { k: "Engaged students", v: "126" },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-xl p-3"
                style={{ backgroundColor: "var(--alt-bg)", border: "1px solid var(--border-color)" }}
              >
                <div className="text-xs" style={{ color: "var(--muted-color)" }}>
                  {s.k}
                </div>
                <div className="text-xl font-semibold mt-1" style={{ color: "var(--text-color)" }}>
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Logos / social proof                                                */
/* ------------------------------------------------------------------ */

function LogoStrip() {
  const logos = ["Stanford", "UC Berkeley", "MIT", "NYU", "Columbia", "Duke"];
  return (
    <section
      className="py-14 sm:py-16"
      style={{ borderTop: "1px solid var(--border-color)", borderBottom: "1px solid var(--border-color)" }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="text-center text-sm" style={{ color: "var(--muted-color)" }}>
          Built for the way modern universities teach
        </p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
          {logos.map((name) => (
            <div
              key={name}
              className="text-center text-lg font-semibold tracking-tight opacity-60 hover:opacity-100 transition-opacity"
              style={{ color: "var(--text-soft)", fontFamily: "var(--font-serif)" }}
            >
              {name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Feature grid                                                        */
/* ------------------------------------------------------------------ */

const FEATURES = [
  {
    title: "Live polling",
    body: "Launch multiple-choice, free-response, or correct-answer polls in seconds. Students join with a code, and no app install is required.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 4 4 5-5" />
      </svg>
    ),
  },
  {
    title: "Student Q&A",
    body: "Upvoted questions surface the topics that matter most. Address the room's real confusion in real time.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    title: "Participation tracking",
    body: "Automatic attendance and response logging. See who's engaged today, this week, this semester.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M20 8v6M23 11h-6" />
      </svg>
    ),
  },
  {
    title: "Session analytics",
    body: "Per-class dashboards reveal accuracy, response time, and stuck concepts at a glance. Drill into any student.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 17V9M15 17v-5M3 9h18" />
      </svg>
    ),
  },
  {
    title: "LMS grade sync",
    body: "One-click sync of participation grades to your LMS gradebook. No CSVs, no copy-paste, no mistakes.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <path d="M21 3v6h-6" />
      </svg>
    ),
  },
  {
    title: "AI class summaries",
    body: "The instant class ends, get an AI-generated summary of what students understood, struggled with, and asked next.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61L12 2z" />
      </svg>
    ),
  },
];

function Features() {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Everything in one platform"
          title="One tool. Every part of class engagement."
          sub="Replace legacy clickers, your Q&A board, your attendance sheet, and your post-class analysis with something that actually feels modern."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="ce-card group" style={{ padding: "1.5rem 1.5rem 1.75rem" }}>
              <div
                className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4"
                style={{
                  color: "var(--primary-color)",
                  backgroundColor: "var(--primary-50)",
                  border: "1px solid color-mix(in srgb, var(--primary-color) 18%, transparent)",
                }}
              >
                {f.icon}
              </div>
              <h3 className="text-lg font-semibold mb-1.5" style={{ color: "var(--text-color)" }}>
                {f.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--muted-color)" }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* AI summary section                                                  */
/* ------------------------------------------------------------------ */

function AISummary() {
  return (
    <section className="py-20 sm:py-28" style={{ backgroundColor: "var(--alt-bg)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <Eyebrow>AI feedback summaries</Eyebrow>
          <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
            Class ends. Insights begin{" "}
            <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>
              automatically.
            </span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--muted-color)" }}>
            CoursEngage reads the room for you. The moment a session ends, you get an AI-generated brief covering what
            landed, what didn't, and which concepts to revisit next class.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Topic-by-topic mastery breakdown",
              "Participation trends across the session",
              "Suggested follow-up content for next session",
              "Class performance highlights in one view",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm">
                <span
                  className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                  style={{ backgroundColor: "var(--primary-color)", color: "#fff" }}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
                <span style={{ color: "var(--text-soft)" }}>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="rounded-2xl p-6 sm:p-7"
          style={{
            backgroundColor: "var(--card-bg)",
            border: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-white text-sm font-bold"
                style={{ background: "var(--gradient-primary)" }}
              >
                ✨
              </span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--text-color)" }}>
                  Today's class summary
                </div>
                <div className="text-xs" style={{ color: "var(--muted-color)" }}>
                  MATH 221 · Lecture 14 · 50 min
                </div>
              </div>
            </div>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ backgroundColor: "var(--primary-50)", color: "var(--primary-color)", fontWeight: 600 }}
            >
              Generated
            </span>
          </div>

          <div className="space-y-4 text-sm" style={{ color: "var(--text-soft)" }}>
            <p>
              Students confidently handled{" "}
              <strong style={{ color: "var(--text-color)" }}>derivatives of polynomials</strong> (92% accuracy), but the
              class struggled with the{" "}
              <strong style={{ color: "var(--primary-color)" }}>chain rule on nested functions</strong> (54%).
            </p>
            <div
              className="rounded-lg p-3"
              style={{ backgroundColor: "var(--alt-bg)", border: "1px solid var(--border-color)" }}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: "var(--muted-color)" }}
              >
                Key class takeaways
              </div>
              <ul className="space-y-1.5">
                <li>· Strong results on derivatives of polynomials</li>
                <li>· Lower confidence on chain rule with nested functions</li>
                <li>· Clear direction for the next class recap</li>
              </ul>
            </div>
            <div
              className="rounded-lg p-3"
              style={{
                backgroundColor: "var(--primary-50)",
                border: "1px solid color-mix(in srgb, var(--primary-color) 20%, transparent)",
              }}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--primary-color)" }}
              >
                Suggested for next class
              </div>
              <p style={{ color: "var(--text-color)" }}>
                Open with 2 chain-rule warm-ups. Recap product vs. chain rule with a side-by-side comparison.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Stats                                                                */
/* ------------------------------------------------------------------ */

function Stats() {
  const items = [
    { k: "2.4×", v: "More class interaction" },
    { k: "+90%", v: "Increase in participation" },
    { k: "3 clicks", v: "To launch a live session" },
    { k: "< 1 min", v: "Typical instructor setup time" },
  ];
  return (
    <section className="py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div
          className="rounded-3xl p-10 sm:p-14 grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10"
          style={{
            backgroundColor: "var(--card-bg)",
            border: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          {items.map((s, i) => (
            <div
              key={s.k}
              className={i > 0 ? "sm:border-l sm:pl-8" : ""}
              style={{ borderColor: "var(--border-color)" }}
            >
              <div className="ce-serif text-5xl sm:text-6xl tracking-tight" style={{ color: "var(--primary-color)" }}>
                {s.k}
              </div>
              <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted-color)" }}>
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Roles                                                                */
/* ------------------------------------------------------------------ */

function Roles() {
  const cards = [
    {
      tag: "For instructors",
      title: "Run class without the friction.",
      points: [
        "Launch polls in two clicks",
        "See live participation as it happens",
        "Auto-graded attendance and accuracy",
      ],
    },
    {
      tag: "For students",
      title: "Show up, speak up, get credit.",
      points: ["Join with a code, no install needed", "Ask anonymously when you need to", "Instant feedback on every answer"],
    },
    {
      tag: "For academic teams",
      title: "Adoption your department can prove.",
      points: ["Privacy-first data handling", "LMS + LTI integration", "Org-wide rollout dashboards"],
    },
  ];
  return (
    <section className="py-20 sm:py-24" style={{ borderTop: "1px solid var(--border-color)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader eyebrow="Built for the whole campus" title="One product, every seat in the lecture hall." />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c) => (
            <div key={c.tag} className="ce-card" style={{ padding: "1.75rem" }}>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--primary-color)" }}>
                {c.tag}
              </div>
              <h3 className="mt-3 text-xl font-semibold" style={{ color: "var(--text-color)" }}>
                {c.title}
              </h3>
              <ul className="mt-5 space-y-2.5 text-sm" style={{ color: "var(--text-soft)" }}>
                {c.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <span
                      className="mt-2 w-1 h-1 rounded-full shrink-0"
                      style={{ backgroundColor: "var(--primary-color)" }}
                    />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* CTA                                                                 */
/* ------------------------------------------------------------------ */

function CTA() {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div
          className="relative overflow-hidden rounded-3xl p-12 sm:p-20 text-center"
          style={{
            backgroundColor: "var(--alt-bg)",
            border: "1px solid var(--border-color)",
          }}
        >
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
            Make the next lecture{" "}
            <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>
              count.
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg max-w-xl mx-auto" style={{ color: "var(--muted-color)" }}>
            Complimentary during the limited Fall 2026 pilot. Your first class is live in under a minute.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <PilotButton className="ce-btn ce-btn-primary text-base px-6 py-3.5">
              Create your first class
            </PilotButton>
            <Link to="/contact" className="ce-btn ce-btn-ghost text-base px-6 py-3.5">
              Talk to our team →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

function Home(/* eslint-disable-next-line no-unused-vars */ { theme } = {}) {
  return (
    <main style={{ backgroundColor: "var(--bg-color)" }}>
      <Hero />
      <LogoStrip />
      <Features />
      <AISummary />
      <Stats />
      <Roles />
      <CTA />
    </main>
  );
}

export default Home;
